import axios from 'axios';

// DeepSeek API 配置
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';
const DEEPSEEK_API_KEY = process.env.REACT_APP_DEEPSEEK_API_KEY || 'sk-f8d98cfbd4924684a96fce0407286a5e';

// KPI 数据类型
export interface KPIData {
  id: string;
  title: string;
  value: string;
  trend: 'up' | 'down' | 'stable';
}

// AI 分析请求参数
export interface AIAnalysisRequest {
  kpiData: KPIData[];
  category: string;
  subcategory: string;
}

// AI 分析响应
export interface AIAnalysisResponse {
  analysis: string;
  suggestions: string[];
  predictions: string[];
  confidence: number;
}

// 构建 AI 提示词
const buildPrompt = (kpiData: KPIData[], category: string, subcategory: string): string => {
  const kpiSummary = kpiData.map(kpi => 
    `${kpi.title}: ${kpi.value} (${kpi.trend === 'up' ? '上升' : kpi.trend === 'down' ? '下降' : '稳定'})`
  ).join(', ');

  return `分析${category}-${subcategory}数据: ${kpiSummary}

请简要分析趋势、提供2-3个建议、预测未来表现。用中文回答，格式简洁。`;
};

// 检查 API 配置
const checkAPIConfig = (): void => {
  if (!DEEPSEEK_API_KEY) {
    throw new Error('⚠️ DeepSeek API 密钥未配置');
  }
  
  if (DEEPSEEK_API_KEY === 'your_api_key_here') {
    throw new Error('⚠️ 请配置有效的 DeepSeek API 密钥');
  }
};

// 调用 DeepSeek API
export const callDeepSeekAPI = async (prompt: string, abortController?: AbortController): Promise<string> => {
  try {
    checkAPIConfig();

    console.log('🔑 使用 API 密钥:', DEEPSEEK_API_KEY.substring(0, 10) + '...');
    console.log('📤 发送请求到 DeepSeek API...');
    
    const response = await axios.post(
      DEEPSEEK_API_URL,
      {
        model: 'deepseek-chat',
        messages: [
          {
            role: 'system',
            content: '你是金融数据分析师，用中文简洁回答。'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
        max_tokens: 800
      },
      {
        headers: {
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`,
          'Content-Type': 'application/json'
        },
        timeout: 15000, // 15秒超时
        signal: abortController?.signal
      }
    );

    console.log('✅ DeepSeek API 调用成功');
    return response.data.choices[0].message.content;
  } catch (error: any) {
    console.error('❌ DeepSeek API 调用失败:', error);
    
    // 详细的错误信息
    if (error.response) {
      console.error('服务器响应错误:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('网络请求失败:', error.request);
    } else {
      console.error('请求配置错误:', error.message);
    }
    
    throw error;
  }
};

// 解析 AI 响应
const parseAIResponse = (response: string): AIAnalysisResponse => {
  try {
    // 清理响应中的格式标记
    let cleanedResponse = response
      .replace(/\*\*/g, '')
      .replace(/###/g, '')
      .replace(/---/g, '')
      .trim();

    // 简化的响应解析逻辑
    const lines = cleanedResponse.split('\n').filter(line => line.trim());
    
    const analysis = lines.slice(0, 3).join('\n'); // 取前3行作为分析
    
    const suggestions = lines
      .filter(line => line.includes('建议') || line.includes('•') || line.includes('-'))
      .slice(0, 3)
      .map(s => s.replace(/[•\-]/g, '').trim());

    const predictions = lines
      .filter(line => line.includes('预测') || line.includes('预计'))
      .slice(0, 2)
      .map(s => s.trim());

    return {
      analysis: analysis || cleanedResponse,
      suggestions: suggestions.length > 0 ? suggestions : ['优化数据监控', '加强用户反馈'],
      predictions: predictions.length > 0 ? predictions : ['数据将持续改善'],
      confidence: 0.8
    };
  } catch (error) {
    console.error('AI 响应解析失败:', error);
    return {
      analysis: response,
      suggestions: ['优化数据监控', '加强用户反馈'],
      predictions: ['数据将持续改善'],
      confidence: 0.7
    };
  }
};



// 主要的 AI 分析函数
export const performAIAnalysis = async (
  kpiData: KPIData[], 
  category: string, 
  subcategory: string,
  abortController?: AbortController
): Promise<AIAnalysisResponse> => {
  console.log('🤖 开始 AI 分析...');
  console.log('📊 分析数据:', kpiData.length, '个 KPI 指标');
  console.log('📋 分析类别:', category, '-', subcategory);
  
  const prompt = buildPrompt(kpiData, category, subcategory);
  console.log('📝 构建的提示词:', prompt.substring(0, 200) + '...');
  
  const response = await callDeepSeekAPI(prompt, abortController);
  console.log('📄 AI 响应:', response.substring(0, 200) + '...');
  
  const result = parseAIResponse(response);
  console.log('✅ AI 分析完成，置信度:', result.confidence);
  
  return result;
}; 